import { Button } from "@/components/ui/button";
import { Edit3, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface TemplateNameEditorProps {
  templateName: string;
  nameEditorState: {
    isEditingName: boolean;
    tempName: string;
    isSavingName: boolean;
    nameError: string | null;
  };
  isSystem?: boolean;
  onUpdateTempName: (name: string) => void;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSaveName: () => Promise<void>;
  onNameChange?: (newName: string) => void;
}

const TemplateNameEditor = ({
  templateName,
  nameEditorState,
  isSystem = false,
  onUpdateTempName,
  onStartEditing,
  onCancelEditing,
  onSaveName,
  onNameChange,
}: TemplateNameEditorProps) => {
  const { t } = useTranslation();
  const { isEditingName, tempName, isSavingName, nameError } = nameEditorState;

  if (isEditingName) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <input
            value={tempName}
            onChange={(e) => onUpdateTempName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSaveName();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onCancelEditing();
              }
            }}
            onBlur={() => {
              // Небольшая задержка чтобы избежать конфликтов с кнопками
              setTimeout(() => {
                if (document.activeElement?.tagName !== 'BUTTON') {
                  onSaveName();
                }
              }, 150);
            }}
            disabled={isSavingName}
            className={`text-2xl font-bold bg-transparent border-none outline-none w-full ${nameError ? "text-red-600" : ""
              } ${isSavingName ? "opacity-50" : ""}`}
            placeholder={t('templates.name_editor.placeholder')}
            maxLength={100}
          />
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={onSaveName}
              disabled={isSavingName || !!nameError}
              className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
            >
              <Check size={16} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onCancelEditing}
              disabled={isSavingName}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
            >
              <X size={16} />
            </Button>
          </div>
        </div>
        {nameError && (
          <p className="text-xs text-red-600">{nameError}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <h1
        className="text-2xl font-bold hover:bg-muted/50 px-2 py-1 rounded cursor-text transition-colors"
        onClick={onStartEditing}
        title={t('templates.name_editor.click_edit_tooltip')}
      >
        {templateName}
      </h1>
      {!isSystem && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onStartEditing}
          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          title={t('templates.name_editor.edit_btn_tooltip')}
        >
          <Edit3 size={16} />
        </Button>
      )}
    </div>
  );
};

export default TemplateNameEditor;